import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FiHome, FiUsers, FiSettings, FiBarChart2, FiShield, FiFileText, FiLogOut, FiMenu, FiX } from 'react-icons/fi';

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const adminMenuItems = [
    { icon: FiHome, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: FiBarChart2, label: 'Analytics', path: '/admin/analytics', key: 'analytics' },
    { icon: FiUsers, label: 'User Management', path: '/admin/users' },

    { icon: FiFileText, label: 'Content Moderation', path: '/admin/content' },
    { icon: FiX, label: 'Deletion Requests', path: '/admin/deletion-requests' },
    { icon: FiShield, label: 'Security', path: '/admin/security' },
    { icon: FiSettings, label: 'System Settings', path: '/admin/settings' },
  ];

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <header className="w-full bg-white shadow-sm border-b border-gray-200">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          {/* Left side - Menu button and Logo */}
          <div className="flex items-center">
            {/* Hamburger Menu - Only visible on mobile */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden text-gray-500 hover:text-gray-700 focus:outline-none mr-4"
            >
              {sidebarOpen ? <FiX className="h-6 w-6" /> : <FiMenu className="h-6 w-6" />}
            </button>

            <div className="flex items-center">
              <div className="flex items-center justify-center w-12 h-12 bg-red-600 rounded-full shadow-lg">
                <FiShield className="w-6 h-6 text-white" />
              </div>
              <div className="flex flex-col ml-3">
                <span className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
                  MarryCenter Admin
                </span>
                <span className="text-xs text-gray-500 -mt-1">Control Panel</span>
              </div>
            </div>
          </div>

          {/* Right side - User info */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3 pl-2 border-l border-gray-200">
              <div className="text-right hidden md:block">
                <p className="text-sm font-medium text-gray-900 leading-tight">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-red-600 font-semibold">
                  Administrator
                </p>
              </div>
              <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                <FiShield className="w-4 h-4" />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area - Sidebar + Page Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className={`
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white shadow-lg border-r border-gray-200 transition-transform duration-300 ease-in-out mt-16 lg:mt-0
        `}>
          <nav className="flex flex-col h-full p-4">
            <div className="space-y-1">
              {adminMenuItems.map((item) => (
                <Link
                  key={item.key || item.path}
                  to={item.path}
                  className="flex items-center px-4 py-3 text-sm font-medium rounded-lg text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors group"
                >
                  <item.icon className="w-5 h-5 mr-3 text-gray-400 group-hover:text-red-500" />
                  {item.label}
                </Link>
              ))}
            </div>

            {/* Logout Button */}
            <div className="mt-auto pt-4 border-t border-gray-200">
              <button
                className="w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                onClick={handleLogout}
              >
                <FiLogOut className="w-5 h-5 mr-3" />
                Logout Admin
              </button>
            </div>
          </nav>
        </aside>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto">
          <main className="p-4 sm:p-6">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
